import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rpcClient } from 'src/api/rpc/client';

const TEST_SUITES_KEY = 'test-suites';

export function useListTestSuites(workflowStepId: string | undefined) {
  return useQuery({
    queryKey: [TEST_SUITES_KEY, 'list', workflowStepId],
    queryFn: () => {
      if (!workflowStepId) throw new Error('workflowStepId required');
      return rpcClient.testSuites.listTestSuites({ workflowStepId });
    },
    enabled: !!workflowStepId,
  });
}

export function useGetTestSuite(id: string | undefined) {
  return useQuery({
    queryKey: [TEST_SUITES_KEY, 'detail', id],
    queryFn: () => {
      if (!id) throw new Error('id required');
      return rpcClient.testSuites.getTestSuite({ id });
    },
    enabled: !!id,
  });
}

export function useCreateTestSuite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      workflowDefinitionId: string;
      workflowStepId: string;
      name: string;
      description?: string;
    }) => rpcClient.testSuites.createTestSuite(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEST_SUITES_KEY] });
    },
  });
}

export function useUpdateTestSuite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      name?: string;
      description?: string | null;
    }) => rpcClient.testSuites.updateTestSuite(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEST_SUITES_KEY] });
    },
  });
}

export function useDeleteTestSuite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string }) =>
      rpcClient.testSuites.deleteTestSuite(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEST_SUITES_KEY] });
    },
  });
}

export function useAddTestCases() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      testSuiteId: string;
      cases: Array<{
        inputData: Record<string, unknown>;
        expectedOutput: Record<string, unknown>;
        sourceStepRunId?: string;
        notes?: string;
      }>;
    }) => rpcClient.testSuites.addTestCases(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEST_SUITES_KEY] });
    },
  });
}

export function useUpdateTestCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      inputData?: Record<string, unknown>;
      expectedOutput?: Record<string, unknown>;
      isActive?: boolean;
      notes?: string | null;
    }) => rpcClient.testSuites.updateTestCase(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEST_SUITES_KEY] });
    },
  });
}

export function useDeleteTestCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string }) =>
      rpcClient.testSuites.deleteTestCase(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEST_SUITES_KEY] });
    },
  });
}

export function useGenerateTestCasesFromHistory() {
  return useMutation({
    mutationFn: (input: {
      workflowStepId: string;
      limit?: number;
      inputFilter?: {
        jsonPath: string;
        equals: string | number | boolean;
      };
    }) => rpcClient.testSuites.generateTestCasesFromHistory(input),
  });
}

export function useRunTestSuite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      testSuiteId: string;
      configOverrides?: Record<string, unknown>;
      configVariantLabel?: string;
    }) => rpcClient.testSuites.runTestSuite(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEST_SUITES_KEY] });
    },
  });
}

export function useGetTestSuiteRun(id: string | undefined) {
  return useQuery({
    queryKey: [TEST_SUITES_KEY, 'run', id],
    queryFn: () => {
      if (!id) throw new Error('id required');
      return rpcClient.testSuites.getTestSuiteRun({ id });
    },
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.testSuiteRun?.status;
      if (status === 'queued' || status === 'running') return 2000;
      return false;
    },
  });
}

export function useListTestSuiteRuns(testSuiteId: string | undefined) {
  return useQuery({
    queryKey: [TEST_SUITES_KEY, 'runs', testSuiteId],
    queryFn: () => {
      if (!testSuiteId) throw new Error('testSuiteId required');
      return rpcClient.testSuites.listTestSuiteRuns({ testSuiteId });
    },
    enabled: !!testSuiteId,
  });
}

export function useSubmitHumanVerdict() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      testCaseResultId: string;
      verdict: 'pass' | 'fail' | 'skip';
    }) => rpcClient.testSuites.submitHumanVerdict(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEST_SUITES_KEY] });
    },
  });
}

export function useSuggestConfigImprovements() {
  return useMutation({
    mutationFn: (input: { testSuiteRunId: string }) =>
      rpcClient.testSuites.suggestConfigImprovements(input),
  });
}

// ── Suite versioning ──────────────────────────────────────────────────────

export function useListTestSuiteVersions(testSuiteId: string | undefined) {
  return useQuery({
    queryKey: [TEST_SUITES_KEY, 'versions', testSuiteId],
    queryFn: () => {
      if (!testSuiteId) throw new Error('testSuiteId required');
      return rpcClient.testSuites.listTestSuiteVersions({ testSuiteId });
    },
    enabled: !!testSuiteId,
  });
}

export function useForkTestSuiteVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { testSuiteId: string; sourceVersionId?: string }) =>
      rpcClient.testSuites.forkTestSuiteVersion(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEST_SUITES_KEY] });
    },
  });
}

export function useFinalizeTestSuiteVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { versionId: string; changelog?: string }) =>
      rpcClient.testSuites.finalizeTestSuiteVersion(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEST_SUITES_KEY] });
    },
  });
}

export function usePublishTestSuiteVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { testSuiteId: string; versionId: string }) =>
      rpcClient.testSuites.publishTestSuiteVersion(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEST_SUITES_KEY] });
    },
  });
}

export function useUpdateTestSuiteVersionChangelog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { versionId: string; changelog: string }) =>
      rpcClient.testSuites.updateTestSuiteVersionChangelog(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEST_SUITES_KEY] });
    },
  });
}

export function useGetTestSuiteTrends(testSuiteId: string | undefined) {
  return useQuery({
    queryKey: [TEST_SUITES_KEY, 'trends', testSuiteId],
    queryFn: () => {
      if (!testSuiteId) throw new Error('testSuiteId required');
      return rpcClient.testSuites.getTestSuiteTrends({ testSuiteId });
    },
    enabled: !!testSuiteId,
  });
}
